// src/components/Admin/IncarichiManagement.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import EmptyState from '../EmptyState';
import ConfirmationModal from '../Common/ConfirmationModal';

const IncarichiManagement = () => {
  // ==============================
  // STATI PRINCIPALI
  // ==============================
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [customIncarichi, setCustomIncarichi] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // ==============================
  // STATI RICERCA E FILTRI
  // ==============================
  const [searchUser, setSearchUser] = useState('');
  const [filters, setFilters] = useState({
    role: 'all',
    incarico: 'all',
    hasIncarico: 'all',
    hasCosto: 'all',
    azienda: 'all'
  });

  // ==============================
  // STATI EDITING INLINE
  // ==============================
  const [editingField, setEditingField] = useState({ userId: null, field: null });
  const [searchIncarico, setSearchIncarico] = useState({});
  const [filteredIncarichi, setFilteredIncarichi] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});
  const [editingCostoOrario, setEditingCostoOrario] = useState({});
  const [editingRole, setEditingRole] = useState({});
  const [editingAzienda, setEditingAzienda] = useState({});

  // ==============================
  // STATI INCARICHI PERSONALIZZATI
  // ==============================
  const [newIncaricoName, setNewIncaricoName] = useState('');
  const [showAddIncaricoForm, setShowAddIncaricoForm] = useState(false);
  const [showCustomIncarichi, setShowCustomIncarichi] = useState(false);

  // ==============================
  // STATI AZIONI IN MASSA
  // ==============================
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [bulkIncaricoValue, setBulkIncaricoValue] = useState('');
  const [bulkCostoValue, setBulkCostoValue] = useState('');
  const [showBulkActions, setShowBulkActions] = useState(false);

  // ==============================
  // STATI MODAL E NOTIFICHE
  // ==============================
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });

  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: 'success'
  });

  // ==============================
  // STATI COLLAPSIBLE - NUOVI
  // ==============================
  const [showStats, setShowStats] = useState(() => {
    // Chiuso di default su mobile, aperto su desktop
    return window.innerWidth > 768;
  });

  // ==============================
  // LISTA INCARICHI PREDEFINITI
  // ==============================
  const incarichiPredefiniti = [
    'Addetto a scarificatrice',
    'Addetto alle pulizie negli uffici',
    'Addetto Impianto cls',
    'Addetto macchine impastatrici di calcestruzzo',
    'Addetti a funzioni di segreteria',
    'Architetto',
    'Asfaltista',
    'Asfaltista stradale a macchina',
    'Asfaltista stradale a mano',
    'Autista',
    'Carpentiere Edile',
    'Carpentieri e falegnami edili',
    'Conduttore di carrello elevatore',
    'Conduttore di macchine per conglomerati biutminosi',
    'Conduttore di mezzi pesanti e camion',
    'Conduttore di pale meccaniche',
    'Conduttori di macchinari mobili per la perforazione in edilizia',
    'Conduttori di macchinari per il movimento terra',
    'Conduttori di macchinari per la produzione di manufatti in cemento e assimilati',
    'Conducente di Autocarro',
    'Conducente di compressore stradale',
    'Conducente di escavatrice meccanica',
    'Geometra',
    'Impiegato Amministrativo',
    'Manovale di Magazzino',
    'Manovale Edile',
    'Manovale stradale',
    'Manovali e personale non qualificato dell\'edilizia civile e professioni assimilate',
    'Meccanico',
    'Meccanico di motori a scoppio',
    'Muratore in calcestruzzo',
    'Palista',
    'Ragioniere Contabile',
    'Rilevatore di dati',
    'Tecnici della gestione di cantieri edili',
    'Tecnici delle costruzioni civili e professioni assimilate',
    'Tecnico della sicurezza sul lavoro',
    'Tecnico di cantiere Edile',
    'Datore di lavoro',
    'Ferraiolo',
    'Rilevatore e disegnatore di prospezioni'
  ];

  // Lista completa incarichi (predefiniti + personalizzati)
  const incarichi = [...incarichiPredefiniti, ...customIncarichi];

  // ==============================
  // RUOLI DISPONIBILI
  // ==============================
  const ruoliDisponibili = [
    { value: 'user', label: 'Operaio', emoji: '👷' },
    { value: 'capo_officina', label: 'Capo Officina', emoji: '🧑‍🔧' },
    { value: 'responsabile', label: 'Responsabile', emoji: '👔' },
    { value: 'hr', label: 'HR', emoji: '🎯' },
    { value: 'admin', label: 'Admin', emoji: '👑' }
  ];

  // ==============================
// AZIENDE DISPONIBILI
// ==============================
const aziendeDisponibili = ['F.D.', 'Isap', 'Eco Smart'];

  // ==============================
  // FUNZIONI HELPER
  // ==============================
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 4000);
  };

  const resetEditing = () => {
    setEditingField({ userId: null, field: null });
    setSearchIncarico({});
    setEditingCostoOrario({});
    setEditingRole({});
    setShowSuggestions({});
    setFilteredIncarichi({});
    setEditingAzienda({});
  };

  const getRoleBadgeClass = (role) => {
    const roleMap = {
      'admin': 'admin',
      'responsabile': 'responsabile',
      'hr': 'hr',
      'user': 'user'
    };
    return roleMap[role] || 'user';
  };

  const getRoleLabel = (role) => {
    const roleObj = ruoliDisponibili.find(r => r.value === role);
    return roleObj ? `${roleObj.emoji} ${roleObj.label}` : '👷 Operaio';
  };

// FUNZIONI HELPER PER LE DATE (da aggiungere all'inizio del componente)
// ============================================

const formatDateForInput = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toISOString().split('T')[0];
};

const handleTipoContrattoChange = async (userId, tipoContratto) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { tipoContratto });
    
    setUsers(prevUsers =>
      prevUsers.map(u => (u.id === userId ? { ...u, tipoContratto } : u))
    );
    
    showNotification('Tipo contratto aggiornato', 'success');
    
    // Se si passa a indeterminato, rimuovi la data di scadenza
    if (tipoContratto === 'indeterminato') {
      await updateDoc(userRef, { dataScadenzaContratto: null });
      setUsers(prevUsers =>
        prevUsers.map(u => (u.id === userId ? { ...u, dataScadenzaContratto: null } : u))
      );
    }
  } catch (error) {
    console.error('Errore aggiornamento tipo contratto:', error);
    showNotification('Errore nell\'aggiornamento', 'error');
  }
};

const handleDataScadenzaContrattoChange = async (userId, dateString) => {
  try {
    const date = dateString ? new Date(dateString) : null;
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { dataScadenzaContratto: date });
    
    setUsers(prevUsers =>
      prevUsers.map(u => (u.id === userId ? { ...u, dataScadenzaContratto: date } : u))
    );
    
    showNotification('Data scadenza contratto aggiornata', 'success');
  } catch (error) {
    console.error('Errore aggiornamento data scadenza:', error);
    showNotification('Errore nell\'aggiornamento', 'error');
  }
};

const handleDataVisitaMedicaChange = async (userId, dateString) => {
  try {
    const date = dateString ? new Date(dateString) : null;
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, { dataScadenzaVisitaMedica: date });
    
    setUsers(prevUsers =>
      prevUsers.map(u => (u.id === userId ? { ...u, dataScadenzaVisitaMedica: date } : u))
    );
    
    showNotification('Data visita medica aggiornata', 'success');
  } catch (error) {
    console.error('Errore aggiornamento visita medica:', error);
    showNotification('Errore nell\'aggiornamento', 'error');
  }
};

 // ==============================
// CARICAMENTO DATI INIZIALE
// ==============================
useEffect(() => {
  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Carica utenti
      const usersCollection = collection(db, "users");
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // ✨ NUOVO: Filtra gli account disabilitati
      const activeUsers = usersList.filter(user => {
        const isActive = user.accountStatus?.active !== false;
        if (!isActive) {
          console.log(`⛔ Escluso account disabilitato da Utenti e Incarichi: ${user.nome || ''} ${user.cognome || ''} (${user.email})`);
        }
        return isActive;
      });
      
      setUsers(activeUsers); // Usa activeUsers invece di usersList
      
      // Carica incarichi personalizzati
      try {
        const incarichiCollection = collection(db, "incarichi_personalizzati");
        const incarichiSnapshot = await getDocs(incarichiCollection);
        const incarichiList = incarichiSnapshot.docs.map(doc => doc.data().nome);
        setCustomIncarichi(incarichiList);
      } catch (incarichiError) {
        console.log("Collezione incarichi_personalizzati non trovata");
      }
      
    } catch (err) {
      console.error("Errore nel caricamento dei dati:", err);
      setError("Impossibile caricare i dati");
    } finally {
      setIsLoading(false);
    }
  };

  fetchData();
}, []);

  // ==============================
// EFFETTO PER GESTIRE IL RESIZE
// ==============================
useEffect(() => {
  const handleResize = () => {
    // Se passi da desktop a mobile, chiudi le stats
    if (window.innerWidth <= 768 && showStats) {
      setShowStats(false);
    }
  };

  window.addEventListener('resize', handleResize);
  return () => window.removeEventListener('resize', handleResize);
}, [showStats]);

 // ==============================
// APPLICAZIONE FILTRI
// ==============================
useEffect(() => {
  let filtered = users;

  // Ricerca per nome/email
  if (searchUser.trim() !== '') {
    const searchLower = searchUser.toLowerCase();
    filtered = filtered.filter(user => {
      const nome = user.nome || '';
      const cognome = user.cognome || '';
      const email = user.email || '';
      const fullName = `${nome} ${cognome}`.trim();
      
      return (
        nome.toLowerCase().includes(searchLower) ||
        cognome.toLowerCase().includes(searchLower) ||
        fullName.toLowerCase().includes(searchLower) ||
        email.toLowerCase().includes(searchLower)
      );
    });
  }

  // Filtro per ruolo
  if (filters.role !== 'all') {
    filtered = filtered.filter(user => {
      const userRole = user.role || 'user';
      return userRole === filters.role;
    });
  }

  // Filtro per incarico specifico
  if (filters.incarico !== 'all') {
    filtered = filtered.filter(user => user.incarico === filters.incarico);
  }

  // Filtro presenza incarico
  if (filters.hasIncarico === 'yes') {
    filtered = filtered.filter(user => user.incarico && user.incarico.trim() !== '');
  } else if (filters.hasIncarico === 'no') {
    filtered = filtered.filter(user => !user.incarico || user.incarico.trim() === '');
  }

  // Filtro presenza costo
  if (filters.hasCosto === 'yes') {
    filtered = filtered.filter(user => user.costoOrario && user.costoOrario > 0);
  } else if (filters.hasCosto === 'no') {
    filtered = filtered.filter(user => !user.costoOrario || user.costoOrario <= 0);
  }

  // ⭐ NUOVO: Filtro per azienda
  if (filters.azienda !== 'all') {
    filtered = filtered.filter(user => user.azienda === filters.azienda);
  }

  setFilteredUsers(filtered);
}, [users, searchUser, filters]);

  // ==============================
  // GESTIONE RICERCA INCARICO
  // ==============================
  useEffect(() => {
    Object.keys(searchIncarico).forEach(userId => {
      const search = searchIncarico[userId];
      if (search && search.length > 0) {
        const filtered = incarichi.filter(inc => 
          inc.toLowerCase().includes(search.toLowerCase())
        );
        setFilteredIncarichi(prev => ({ ...prev, [userId]: filtered }));
        setShowSuggestions(prev => ({ ...prev, [userId]: filtered.length > 0 }));
      } else {
        setFilteredIncarichi(prev => ({ ...prev, [userId]: [] }));
        setShowSuggestions(prev => ({ ...prev, [userId]: false }));
      }
    });
  }, [searchIncarico, incarichi]);

  // ==============================
  // FUNZIONI DI MODIFICA
  // ==============================
  const handleRoleChange = async (userId, newRole) => {
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { role: newRole });
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, role: newRole } : user
        )
      );
      
      resetEditing();
      showNotification('Ruolo aggiornato con successo', 'success');
    } catch (err) {
      console.error("Errore nell'aggiornamento del ruolo:", err);
      showNotification('Errore nell\'aggiornamento del ruolo', 'error');
    }
  };

  const handleAziendaChange = async (userId, newAzienda) => {
    if (!newAzienda || !aziendeDisponibili.includes(newAzienda)) {
      showNotification('Seleziona un\'azienda valida', 'warning');
      return;
    }
  
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { azienda: newAzienda });
      
      setUsers(prevUsers =>
        prevUsers.map(u => (u.id === userId ? { ...u, azienda: newAzienda } : u))
      );
      
      const user = users.find(u => u.id === userId);
      const fullName = user?.nome && user?.cognome 
        ? `${user.nome} ${user.cognome}` 
        : user?.email || 'Utente';
      
      showNotification(`Azienda aggiornata per ${fullName}`, 'success');
      resetEditing();
    } catch (err) {
      console.error("Errore nell'aggiornamento dell'azienda:", err);
      showNotification('Errore nell\'aggiornamento', 'error');
    }
  };

  const handleIncaricoChange = async (userId, newIncarico) => {
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { incarico: newIncarico });
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, incarico: newIncarico } : user
        )
      );
      
      resetEditing();
      showNotification('Incarico aggiornato con successo', 'success');
    } catch (err) {
      console.error("Errore nell'aggiornamento dell'incarico:", err);
      showNotification('Errore nell\'aggiornamento dell\'incarico', 'error');
    }
  };

  const handleCostoOrarioChange = async (userId, newCosto) => {
    try {
      const costoFloat = parseFloat(newCosto);
      if (isNaN(costoFloat) || costoFloat < 0) {
        showNotification('Inserisci un costo valido', 'error');
        return;
      }

      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, { costoOrario: costoFloat });
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, costoOrario: costoFloat } : user
        )
      );
      
      resetEditing();
      showNotification('Costo orario aggiornato con successo', 'success');
    } catch (err) {
      console.error("Errore nell'aggiornamento del costo:", err);
      showNotification('Errore nell\'aggiornamento del costo', 'error');
    }
  };

  const handleIncaricoSelect = (userId, incarico) => {
    setSearchIncarico(prev => ({ ...prev, [userId]: incarico }));
    setShowSuggestions(prev => ({ ...prev, [userId]: false }));
  };

  // ==============================
  // GESTIONE INCARICHI PERSONALIZZATI
  // ==============================
  const handleAddCustomIncarico = async () => {
    const trimmedName = newIncaricoName.trim();
    
    if (!trimmedName) {
      showNotification('Inserisci un nome valido', 'error');
      return;
    }

    if (incarichi.some(inc => inc.toLowerCase() === trimmedName.toLowerCase())) {
      showNotification('Questo incarico esiste già', 'warning');
      return;
    }

    try {
      const incarichiCollection = collection(db, "incarichi_personalizzati");
      await addDoc(incarichiCollection, {
        nome: trimmedName,
        createdAt: new Date().toISOString()
      });
      
      setCustomIncarichi(prev => [...prev, trimmedName]);
      setNewIncaricoName('');
      setShowAddIncaricoForm(false);
      showNotification(`Incarico "${trimmedName}" aggiunto con successo`, 'success');
    } catch (err) {
      console.error("Errore nell'aggiunta dell'incarico:", err);
      showNotification('Errore nell\'aggiunta dell\'incarico', 'error');
    }
  };

  const handleDeleteCustomIncarico = async (incaricoName) => {
    const usageCount = users.filter(u => u.incarico === incaricoName).length;
    
    if (usageCount > 0) {
      showNotification(
        `Impossibile eliminare: ${usageCount} dipendenti hanno questo incarico`,
        'warning'
      );
      return;
    }

    setConfirmModal({
      isOpen: true,
      title: 'Elimina Incarico Personalizzato',
      message: `Sei sicuro di voler eliminare l'incarico "${incaricoName}"?`,
      onConfirm: async () => {
        try {
          const incarichiCollection = collection(db, "incarichi_personalizzati");
          const incarichiSnapshot = await getDocs(incarichiCollection);
          
          const docToDelete = incarichiSnapshot.docs.find(
            doc => doc.data().nome === incaricoName
          );
          
          if (docToDelete) {
            await deleteDoc(doc(db, "incarichi_personalizzati", docToDelete.id));
            setCustomIncarichi(prev => prev.filter(inc => inc !== incaricoName));
            showNotification(`Incarico "${incaricoName}" eliminato`, 'success');
          }
        } catch (err) {
          console.error("Errore nell'eliminazione:", err);
          showNotification('Errore nell\'eliminazione dell\'incarico', 'error');
        }
      }
    });
  };

  // ==============================
  // AZIONI IN MASSA
  // ==============================
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const handleBulkIncaricoUpdate = async () => {
    if (!bulkIncaricoValue || selectedUsers.size === 0) {
      showNotification('Seleziona utenti e incarico', 'warning');
      return;
    }

    try {
      const updatePromises = Array.from(selectedUsers).map(userId => {
        const userRef = doc(db, "users", userId);
        return updateDoc(userRef, { incarico: bulkIncaricoValue });
      });

      await Promise.all(updatePromises);
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          selectedUsers.has(user.id) 
            ? { ...user, incarico: bulkIncaricoValue } 
            : user
        )
      );

      setSelectedUsers(new Set());
      setBulkIncaricoValue('');
      showNotification(`Incarico aggiornato per ${selectedUsers.size} dipendenti`, 'success');
    } catch (err) {
      console.error("Errore nell'aggiornamento di massa:", err);
      showNotification('Errore nell\'aggiornamento', 'error');
    }
  };

  const handleBulkCostoUpdate = async () => {
    if (!bulkCostoValue || selectedUsers.size === 0) {
      showNotification('Seleziona utenti e inserisci costo', 'warning');
      return;
    }

    const costoFloat = parseFloat(bulkCostoValue);
    if (isNaN(costoFloat) || costoFloat < 0) {
      showNotification('Inserisci un costo valido', 'error');
      return;
    }

    try {
      const updatePromises = Array.from(selectedUsers).map(userId => {
        const userRef = doc(db, "users", userId);
        return updateDoc(userRef, { costoOrario: costoFloat });
      });

      await Promise.all(updatePromises);
      
      setUsers(prevUsers => 
        prevUsers.map(user => 
          selectedUsers.has(user.id) 
            ? { ...user, costoOrario: costoFloat } 
            : user
        )
      );

      setSelectedUsers(new Set());
      setBulkCostoValue('');
      showNotification(`Costo aggiornato per ${selectedUsers.size} dipendenti`, 'success');
    } catch (err) {
      console.error("Errore nell'aggiornamento di massa:", err);
      showNotification('Errore nell\'aggiornamento', 'error');
    }
  };

  // ==============================
// CALCOLO STATISTICHE
// ==============================
const stats = {
  totalUsers: users.length,
  withIncarico: users.filter(u => u.incarico && u.incarico.trim() !== '').length,
  withoutIncarico: users.filter(u => !u.incarico || u.incarico.trim() === '').length,
  withCosto: users.filter(u => u.costoOrario && u.costoOrario > 0).length,
  withoutCosto: users.filter(u => !u.costoOrario || u.costoOrario <= 0).length,
  missingBoth: users.filter(u => 
    (!u.incarico || u.incarico.trim() === '') && 
    (!u.costoOrario || u.costoOrario <= 0)
  ).length,
  byRole: {
    admin: users.filter(u => u.role === 'admin').length,
    responsabile: users.filter(u => u.role === 'responsabile').length,
    hr: users.filter(u => u.role === 'hr').length,
    user: users.filter(u => !u.role || u.role === 'user').length
  },
  byAzienda: {
    fd: users.filter(u => u.azienda === 'F.D.').length,
    isap: users.filter(u => u.azienda === 'Isap').length,
    ecosmart: users.filter(u => u.azienda === 'Eco Smart').length,
    noAzienda: users.filter(u => !u.azienda || u.azienda.trim() === '').length
  }
};

  const uniqueIncarichi = [...new Set(users.filter(u => u.incarico).map(u => u.incarico))];

  // ==============================
  // RENDERING
  // ==============================
  if (isLoading) {
    return (
      <div className="incarichi-management">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div className="spinner"></div>
          <p>Caricamento dati...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="incarichi-management">
        <EmptyState
          icon="⚠️"
          title="Errore"
          message={error}
          actionText="Ricarica pagina"
          onAction={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="incarichi-management">
      {/* NOTIFICA */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-content">
            <span className="notification-message">{notification.message}</span>
            <button 
              className="notification-close"
              onClick={() => setNotification(prev => ({ ...prev, show: false }))}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="incarichi-header">
        <h2>👥 Gestione Incarichi & Costi</h2>
        <p>Gestisci ruoli, incarichi e costi orari di tutti i dipendenti</p>
      </div>

{/* STATISTICHE COLLAPSIBILI */}
<div className="stats-section">
  <div 
    className={`stats-header ${showStats ? 'expanded' : 'collapsed'}`}
    onClick={() => setShowStats(!showStats)}
  >
    <div className="stats-title">
      <span>📊 Statistiche Dipendenti</span>
      <span className="stats-count-badge">{stats.totalUsers}</span>
    </div>
    <span className="toggle-icon">{showStats ? '▼' : '▶'}</span>
  </div>
  
  {showStats && (
    <>
      {/* STATISTICHE GENERALI */}
      <div className="incarichi-stats">
        <div className="stat-card">
          <span className="stat-number">{stats.totalUsers}</span>
          <span className="stat-label">👥 Totale Dipendenti</span>
        </div>
        
        <div className="stat-card success">
          <span className="stat-number">{stats.withIncarico}</span>
          <span className="stat-label">🏗️ Con Incarico</span>
        </div>
        
        <div className="stat-card warning">
          <span className="stat-number">{stats.withoutIncarico}</span>
          <span className="stat-label">⚠️ Senza Incarico</span>
        </div>
        
        <div className="stat-card success">
          <span className="stat-number">{stats.withCosto}</span>
          <span className="stat-label">💰 Con Costo Orario</span>
        </div>
        
        <div className="stat-card warning">
          <span className="stat-number">{stats.withoutCosto}</span>
          <span className="stat-label">⚠️ Senza Costo</span>
        </div>
        
        {stats.missingBoth > 0 && (
          <div className="stat-card danger">
            <span className="stat-number">{stats.missingBoth}</span>
            <span className="stat-label">🚨 Incompleti</span>
          </div>
        )}
      </div>

      {/* STATISTICHE RUOLI */}
      <div className="stats-subtitle">
        <h4>👔 Distribuzione per Ruolo</h4>
      </div>
      <div className="incarichi-stats">
        <div className="stat-card info">
          <span className="stat-number">{stats.byRole.admin}</span>
          <span className="stat-label">👑 Admin</span>
        </div>
        <div className="stat-card info">
          <span className="stat-number">{stats.byRole.responsabile}</span>
          <span className="stat-label">👔 Responsabili</span>
        </div>
        <div className="stat-card info">
          <span className="stat-number">{stats.byRole.hr}</span>
          <span className="stat-label">🎯 HR</span>
        </div>
        <div className="stat-card info">
          <span className="stat-number">{stats.byRole.user}</span>
          <span className="stat-label">👷 Operai</span>
        </div>
      </div>

      {/* STATISTICHE AZIENDE */}
      <div className="stats-subtitle">
        <h4>🏢 Distribuzione per Azienda</h4>
      </div>
      <div className="incarichi-stats">
        <div className="stat-card primary">
          <span className="stat-number">{stats.byAzienda.fd}</span>
          <span className="stat-label">🏢 F.D.</span>
        </div>
        <div className="stat-card primary">
          <span className="stat-number">{stats.byAzienda.isap}</span>
          <span className="stat-label">🏢 Isap</span>
        </div>
        <div className="stat-card primary">
          <span className="stat-number">{stats.byAzienda.ecosmart}</span>
          <span className="stat-label">🏢 Eco Smart</span>
        </div>
        <div className="stat-card warning">
          <span className="stat-number">{stats.byAzienda.noAzienda}</span>
          <span className="stat-label">⚠️ Senza Azienda</span>
        </div>
      </div>
    </>
  )}
</div>

      {/* INCARICHI PERSONALIZZATI */}
      <div className="custom-incarichi-section">
        <div className="section-header">
          <h3>🎯 Incarichi Personalizzati</h3>
          <button
            className="btn-primary"
            onClick={() => setShowAddIncaricoForm(!showAddIncaricoForm)}
          >
            {showAddIncaricoForm ? '✗ Annulla' : '➕ Aggiungi Incarico'}
          </button>
        </div>

        {showAddIncaricoForm && (
          <div className="add-incarico-form">
            <input
              type="text"
              value={newIncaricoName}
              onChange={(e) => setNewIncaricoName(e.target.value)}
              placeholder="Nome nuovo incarico..."
              onKeyPress={(e) => e.key === 'Enter' && handleAddCustomIncarico()}
            />
            <button className="btn-success" onClick={handleAddCustomIncarico}>
              ✓ Crea Incarico
            </button>
          </div>
        )}

        {customIncarichi.length > 0 && (
          <div className={`custom-incarichi-list ${showCustomIncarichi ? '' : 'collapsed'}`}>
            <h4 onClick={() => setShowCustomIncarichi(!showCustomIncarichi)}>
              Incarichi Aggiunti
              <span className="custom-incarichi-count">{customIncarichi.length}</span>
            </h4>
            <div className="incarichi-dropdown">
              {customIncarichi.map(incarico => {
                const usageCount = users.filter(u => u.incarico === incarico).length;
                return (
                  <div key={incarico} className="custom-incarico-item">
                    <div>
                      <span className="incarico-name">🏗️ {incarico}</span>
                      {usageCount > 0 && (
                        <span className="usage-count"> (utilizzato da {usageCount} dipendenti)</span>
                      )}
                    </div>
                    <button
                      className="btn-danger btn-small"
                      onClick={() => handleDeleteCustomIncarico(incarico)}
                      disabled={usageCount > 0}
                      title={usageCount > 0 ? "Non può essere eliminato perché in uso" : "Elimina incarico"}
                    >
                      🗑️ Elimina
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* FILTRI E RICERCA */}
<div className="incarichi-filters">
  <div className="search-box">
    <input
      type="text"
      placeholder="🔍 Cerca utente per nome, cognome o email..."
      value={searchUser}
      onChange={(e) => setSearchUser(e.target.value)}
    />
  </div>

  <div className="filters-row">
    <select
      value={filters.role}
      onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
    >
      <option value="all">👥 Tutti i Ruoli</option>
      <option value="admin">👑 Admin</option>
      <option value="responsabile">👔 Responsabile</option>
      <option value="hr">🎯 HR</option>
      <option value="user">👷 Operaio</option>
    </select>

    <select
      value={filters.incarico}
      onChange={(e) => setFilters(prev => ({ ...prev, incarico: e.target.value }))}
    >
      <option value="all">🏗️ Tutti gli Incarichi</option>
      {incarichi.map(inc => (
        <option key={inc} value={inc}>{inc}</option>
      ))}
    </select>

    <select
      value={filters.hasIncarico}
      onChange={(e) => setFilters(prev => ({ ...prev, hasIncarico: e.target.value }))}
    >
      <option value="all">📋 Stato Incarico</option>
      <option value="yes">✅ Con Incarico</option>
      <option value="no">❌ Senza Incarico</option>
    </select>

    <select
      value={filters.hasCosto}
      onChange={(e) => setFilters(prev => ({ ...prev, hasCosto: e.target.value }))}
    >
      <option value="all">💰 Stato Costo</option>
      <option value="yes">✅ Con Costo</option>
      <option value="no">❌ Senza Costo</option>
    </select>

    {/* ⭐ NUOVO FILTRO AZIENDA */}
    <select
      value={filters.azienda}
      onChange={(e) => setFilters(prev => ({ ...prev, azienda: e.target.value }))}
    >
      <option value="all">🏢 Tutte le Aziende</option>
      <option value="F.D.">🏢 F.D.</option>
      <option value="Isap">🏢 Isap</option>
      <option value="Eco Smart">🏢 Eco Smart</option>
    </select>
  </div>
</div>

      {/* AZIONI IN MASSA */}
      {selectedUsers.size > 0 && (
        <div className="bulk-actions-panel">
          <div className="bulk-header">
            <span className="bulk-count">✅ {selectedUsers.size} selezionati</span>
            <button 
              className="btn-secondary btn-small"
              onClick={() => setSelectedUsers(new Set())}
            >
              Deseleziona tutti
            </button>
          </div>
          
          <div className="bulk-actions">
            <div className="bulk-action-group">
              <select
                value={bulkIncaricoValue}
                onChange={(e) => setBulkIncaricoValue(e.target.value)}
                className="bulk-select"
              >
                <option value="">Seleziona incarico...</option>
                {incarichi.map(inc => (
                  <option key={inc} value={inc}>{inc}</option>
                ))}
              </select>
              <button 
                className="btn-primary"
                onClick={handleBulkIncaricoUpdate}
                disabled={!bulkIncaricoValue}
              >
                🏗️ Applica Incarico
              </button>
            </div>

            <div className="bulk-action-group">
              <div className="bulk-cost-input">
                <span>€</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={bulkCostoValue}
                  onChange={(e) => setBulkCostoValue(e.target.value)}
                  placeholder="0.00"
                />
                <span>/ora</span>
              </div>
              <button 
                className="btn-info"
                onClick={handleBulkCostoUpdate}
                disabled={!bulkCostoValue}
              >
                💰 Applica Costo
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TABELLA/CARDS DIPENDENTI */}
      {filteredUsers.length === 0 ? (
        <EmptyState
          icon="🔍"
          title="Nessun risultato"
          message="Nessun dipendente corrisponde ai filtri selezionati"
          actionText="Reset Filtri"
          onAction={() => {
            setSearchUser('');
            setFilters({
              role: 'all',
              incarico: 'all',
              hasIncarico: 'all',
              hasCosto: 'all'
            });
          }}
        />
      ) : (
        <>
          {/* VISTA DESKTOP - Tabella */}
          <div className="table-container hide-mobile">
            <table className="incarichi-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={selectedUsers.size === filteredUsers.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>👤 Dipendente</th>
                  <th>🎭 Ruolo</th>
                  <th>🏢 Azienda</th>
                  <th>🏗️ Incarico</th>
                  <th>💰 Costo Orario</th>
                  <th>⚙️ Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const fullName = `${user.nome || ''} ${user.cognome || ''}`.trim() || 'Senza nome';
                  const isEditingIncarico = editingField.userId === user.id && editingField.field === 'incarico';
                  const isEditingCosto = editingField.userId === user.id && editingField.field === 'costoOrario';
                  const isEditingRoleField = editingField.userId === user.id && editingField.field === 'role';

                  return (
                    <tr 
                      key={user.id}
                      className={`
                        ${!user.incarico ? 'no-incarico' : ''} 
                        ${(!user.costoOrario || user.costoOrario <= 0) ? 'no-costo' : ''}
                      `}
                    >
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.id)}
                          onChange={() => toggleUserSelection(user.id)}
                        />
                      </td>
                      
                      <td className="user-cell">
                        <div className="user-info">
                          <span className="user-name">{fullName}</span>
                          <span className="user-email">{user.email}</span>
                        </div>
                      </td>
                      
                      <td className="role-cell">
                        {isEditingRoleField ? (
                          <div className="role-edit-container">
                            <select
                              value={editingRole[user.id] || user.role || 'user'}
                              onChange={(e) => setEditingRole(prev => ({ ...prev, [user.id]: e.target.value }))}
                              className="role-select"
                              autoFocus
                            >
                              {ruoliDisponibili.map(role => (
                                <option key={role.value} value={role.value}>
                                  {role.emoji} {role.label}
                                </option>
                              ))}
                            </select>
                            <div className="edit-actions">
                              <button
                                className="btn-success btn-small"
                                onClick={() => handleRoleChange(user.id, editingRole[user.id])}
                              >
                                ✓ Salva
                              </button>
                              <button
                                className="btn-secondary btn-small"
                                onClick={resetEditing}
                              >
                                ✗ Annulla
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                            {getRoleLabel(user.role)}
                          </span>
                        )}
                      </td>

                      <td>
  {editingField.userId === user.id && editingField.field === 'azienda' ? (
    <div className="role-edit-container">
      <select
        className="role-select"
        value={editingAzienda[user.id] || user.azienda || ''}
        onChange={(e) => setEditingAzienda({ ...editingAzienda, [user.id]: e.target.value })}
        autoFocus
      >
        <option value="">Seleziona azienda...</option>
        {aziendeDisponibili.map(azienda => (
          <option key={azienda} value={azienda}>
            {azienda}
          </option>
        ))}
      </select>
      <div className="edit-actions">
        <button
          className="btn-success btn-small"
          onClick={() => handleAziendaChange(user.id, editingAzienda[user.id])}
        >
          ✓
        </button>
        <button
          className="btn-secondary btn-small"
          onClick={resetEditing}
        >
          ✗
        </button>
      </div>
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span className={`badge badge-azienda ${user.azienda ? '' : 'badge-empty'}`}>
        {user.azienda || 'Non assegnata'}
      </span>
    </div>
  )}
</td>
                      
                      <td className="incarico-cell">
                        {isEditingIncarico ? (
                          <div className="incarico-edit-container">
                            <input
                              type="text"
                              value={searchIncarico[user.id] || ''}
                              onChange={(e) => setSearchIncarico(prev => ({ ...prev, [user.id]: e.target.value }))}
                              placeholder="Cerca incarico..."
                              className="incarico-search-input"
                              autoFocus
                            />

                            {showSuggestions[user.id] && filteredIncarichi[user.id]?.length > 0 && (
                              <ul className="suggestions-dropdown">
                                {filteredIncarichi[user.id].slice(0, 5).map(incarico => (
                                  <li
                                    key={incarico}
                                    onClick={() => handleIncaricoSelect(user.id, incarico)}
                                    className="suggestion-item"
                                  >
                                    🏗️ {incarico}
                                  </li>
                                ))}
                              </ul>
                            )}

                            <div className="edit-actions">
                              <button
                                className="btn-success btn-small"
                                onClick={() => {
                                  if (searchIncarico[user.id]?.trim()) {
                                    handleIncaricoChange(user.id, searchIncarico[user.id].trim());
                                  } else {
                                    resetEditing();
                                  }
                                }}
                              >
                                ✓ Salva
                              </button>
                              <button
                                className="btn-secondary btn-small"
                                onClick={resetEditing}
                              >
                                ✗ Annulla
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="incarico-display">
                            <span className={`incarico-value ${!user.incarico ? 'empty' : ''}`}>
                              {user.incarico ? `🏗️ ${user.incarico}` : '❌ Nessun incarico'}
                            </span>
                          </div>
                        )}
                      </td>
                      
                      <td className="costo-orario-cell">
                        {isEditingCosto ? (
                          <div className="costo-edit-container">
                            <div className="input-with-currency">
                              <span className="currency-symbol">€</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={editingCostoOrario[user.id] || ''}
                                placeholder="0.00"
                                onChange={(e) => setEditingCostoOrario(prev => ({ ...prev, [user.id]: e.target.value }))}
                                className="costo-orario-input"
                                autoFocus
                              />
                              <span className="currency-suffix">/ora</span>
                            </div>
                            <div className="edit-actions">
                              <button
                                className="btn-success btn-small"
                                onClick={() => {
                                  if (editingCostoOrario[user.id]?.trim()) {
                                    handleCostoOrarioChange(user.id, editingCostoOrario[user.id]);
                                  } else {
                                    resetEditing();
                                  }
                                }}
                              >
                                ✓ Salva
                              </button>
                              <button
                                className="btn-secondary btn-small"
                                onClick={resetEditing}
                              >
                                ✗ Annulla
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="costo-orario-display">
                            <span className={`costo-orario-value ${!user.costoOrario || user.costoOrario <= 0 ? 'empty' : ''}`}>
                              {user.costoOrario && user.costoOrario > 0 
                                ? `💰 €${parseFloat(user.costoOrario).toFixed(2)}/ora` 
                                : '❌ Non impostato'}
                            </span>
                          </div>
                        )}
                      </td>
                      
                      <td className="actions-cell">
                        {!isEditingIncarico && !isEditingCosto && !isEditingRoleField ? (
                          <div className="action-buttons">
                            <button
                              className="btn-accent btn-small"
                              onClick={() => {
                                setEditingField({ userId: user.id, field: 'role' });
                                setEditingRole(prev => ({ ...prev, [user.id]: user.role || 'user' }));
                              }}
                              title="Modifica ruolo"
                            >
                              🎭 Ruolo
                            </button>
                            <button
  className="btn-info btn-small"
  onClick={() => {
    setEditingField({ userId: user.id, field: 'azienda' });
    setEditingAzienda({ [user.id]: user.azienda || '' });
  }}
  title="Modifica azienda"
>
  🏢 Azienda
</button>
                            <button
                              className="btn-primary btn-small"
                              onClick={() => {
                                setEditingField({ userId: user.id, field: 'incarico' });
                                setSearchIncarico(prev => ({ ...prev, [user.id]: user.incarico || '' }));
                              }}
                              title="Modifica incarico"
                            >
                              🏗️ Incarico
                            </button>
                            <button
                              className="btn-info btn-small"
                              onClick={() => {
                                setEditingField({ userId: user.id, field: 'costoOrario' });
                                setEditingCostoOrario(prev => ({ 
                                  ...prev, 
                                  [user.id]: user.costoOrario ? user.costoOrario.toString() : '' 
                                }));
                              }}
                              title="Modifica costo orario"
                            >
                              💰 Costo
                            </button>
                            {(user.incarico || (user.costoOrario && user.costoOrario > 0)) && (
                              <button
                                className="btn-danger btn-small"
                                onClick={() => {
                                  setConfirmModal({
                                    isOpen: true,
                                    title: 'Reset Dati Dipendente',
                                    message: `Vuoi rimuovere incarico e costo orario di ${fullName}?`,
                                    onConfirm: async () => {
                                      try {
                                        const userRef = doc(db, "users", user.id);
                                        await updateDoc(userRef, { 
                                          incarico: '',
                                          costoOrario: 0 
                                        });
                                        setUsers(prevUsers => 
                                          prevUsers.map(u => 
                                            u.id === user.id 
                                              ? { ...u, incarico: '', costoOrario: 0 } 
                                              : u
                                          )
                                        );
                                        showNotification(`Dati rimossi per ${fullName}`, 'success');
                                      } catch (err) {
                                        showNotification('Errore nella rimozione', 'error');
                                      }
                                    }
                                  });
                                }}
                                title="Rimuovi tutto"
                              >
                                🗑️ Reset
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="editing-indicator">
                            ✏️ In modifica...
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

{/* VISTA MOBILE - Cards */}
<div className="users-cards-container hide-desktop">
  {filteredUsers.map((user) => {
    const fullName = `${user.nome || ''} ${user.cognome || ''}`.trim() || 'Senza nome';
    const isEditingIncarico = editingField.userId === user.id && editingField.field === 'incarico';
    const isEditingCosto = editingField.userId === user.id && editingField.field === 'costoOrario';
    const isEditingRoleField = editingField.userId === user.id && editingField.field === 'role';
    const isEditingAziendaField = editingField.userId === user.id && editingField.field === 'azienda'; // ← NUOVO

    return (
      <div 
        key={user.id} 
        className={`user-card ${!user.incarico ? 'no-incarico' : ''} ${(!user.costoOrario || user.costoOrario <= 0) ? 'no-costo' : ''} ${!user.azienda ? 'no-azienda' : ''}`}
      >
        {/* Header Card */}
        <div className="card-header">
          <input
            type="checkbox"
            checked={selectedUsers.has(user.id)}
            onChange={() => toggleUserSelection(user.id)}
            className="card-checkbox"
          />
          <div className="card-user-info">
            <h3 className="card-user-name">{fullName}</h3>
            <p className="card-user-email">{user.email}</p>
          </div>
        </div>

        {/* Ruolo */}
        <div className="card-field">
          <label className="card-label">🎭 Ruolo</label>
          {isEditingRoleField ? (
            <div className="card-field-edit">
              <select
                value={editingRole[user.id] || user.role || 'user'}
                onChange={(e) => setEditingRole(prev => ({ ...prev, [user.id]: e.target.value }))}
                className="card-select"
              >
                {ruoliDisponibili.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.emoji} {role.label}
                  </option>
                ))}
              </select>
              <div className="card-actions">
                <button
                  className="btn-success btn-small"
                  onClick={() => handleRoleChange(user.id, editingRole[user.id])}
                >
                  ✓
                </button>
                <button
                  className="btn-secondary btn-small"
                  onClick={resetEditing}
                >
                  ✗
                </button>
              </div>
            </div>
          ) : (
            <div className="card-field-display">
              <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                {getRoleLabel(user.role)}
              </span>
              <button
                className="btn-edit-inline"
                onClick={() => {
                  setEditingField({ userId: user.id, field: 'role' });
                  setEditingRole(prev => ({ ...prev, [user.id]: user.role || 'user' }));
                }}
              >
                ✏️
              </button>
            </div>
          )}
        </div>

        {/* ⭐ NUOVO: Azienda */}
        <div className="card-field">
          <label className="card-label">🏢 Azienda</label>
          {isEditingAziendaField ? (
            <div className="card-field-edit">
              <select
                value={editingAzienda[user.id] || user.azienda || ''}
                onChange={(e) => setEditingAzienda(prev => ({ ...prev, [user.id]: e.target.value }))}
                className="card-select"
              >
                <option value="">Seleziona azienda...</option>
                {aziendeDisponibili.map(azienda => (
                  <option key={azienda} value={azienda}>
                    {azienda}
                  </option>
                ))}
              </select>
              <div className="card-actions">
                <button
                  className="btn-success btn-small"
                  onClick={() => handleAziendaChange(user.id, editingAzienda[user.id])}
                >
                  ✓
                </button>
                <button
                  className="btn-secondary btn-small"
                  onClick={resetEditing}
                >
                  ✗
                </button>
              </div>
            </div>
          ) : (
            <div className="card-field-display">
              <span className={`badge-azienda ${!user.azienda ? 'badge-empty' : ''}`}>
                {user.azienda || '❌ Non assegnata'}
              </span>
              <button
                className="btn-edit-inline"
                onClick={() => {
                  setEditingField({ userId: user.id, field: 'azienda' });
                  setEditingAzienda(prev => ({ ...prev, [user.id]: user.azienda || '' }));
                }}
              >
                ✏️
              </button>
            </div>
          )}
        </div>

        {/* Incarico */}
        <div className="card-field">
          <label className="card-label">🏗️ Incarico</label>
          {isEditingIncarico ? (
            <div className="card-field-edit">
              <input
                type="text"
                value={searchIncarico[user.id] || ''}
                onChange={(e) => setSearchIncarico(prev => ({ ...prev, [user.id]: e.target.value }))}
                placeholder="Cerca incarico..."
                className="card-input"
              />
              {showSuggestions[user.id] && filteredIncarichi[user.id]?.length > 0 && (
                <ul className="card-suggestions">
                  {filteredIncarichi[user.id].slice(0, 5).map(incarico => (
                    <li
                      key={incarico}
                      onClick={() => handleIncaricoSelect(user.id, incarico)}
                    >
                      🏗️ {incarico}
                    </li>
                  ))}
                </ul>
              )}
              <div className="card-actions">
                <button
                  className="btn-success btn-small"
                  onClick={() => {
                    if (searchIncarico[user.id]?.trim()) {
                      handleIncaricoChange(user.id, searchIncarico[user.id].trim());
                    } else {
                      resetEditing();
                    }
                  }}
                >
                  ✓ Salva
                </button>
                <button
                  className="btn-secondary btn-small"
                  onClick={resetEditing}
                >
                  ✗
                </button>
              </div>
            </div>
          ) : (
            <div className="card-field-display">
              <span className={`card-value ${!user.incarico ? 'empty' : ''}`}>
                {user.incarico || '❌ Nessun incarico'}
              </span>
              <button
                className="btn-edit-inline"
                onClick={() => {
                  setEditingField({ userId: user.id, field: 'incarico' });
                  setSearchIncarico(prev => ({ ...prev, [user.id]: user.incarico || '' }));
                }}
              >
                ✏️
              </button>
            </div>
          )}
        </div>

        {/* Costo Orario */}
        <div className="card-field">
          <label className="card-label">💰 Costo Orario</label>
          {isEditingCosto ? (
            <div className="card-field-edit">
              <div className="card-input-currency">
                <span>€</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editingCostoOrario[user.id] || ''}
                  placeholder="0.00"
                  onChange={(e) => setEditingCostoOrario(prev => ({ ...prev, [user.id]: e.target.value }))}
                  className="card-input"
                />
                <span>/ora</span>
              </div>
              <div className="card-actions">
                <button
                  className="btn-success btn-small"
                  onClick={() => {
                    if (editingCostoOrario[user.id]?.trim()) {
                      handleCostoOrarioChange(user.id, editingCostoOrario[user.id]);
                    } else {
                      resetEditing();
                    }
                  }}
                >
                  ✓ Salva
                </button>
                <button
                  className="btn-secondary btn-small"
                  onClick={resetEditing}
                >
                  ✗
                </button>
              </div>
            </div>
          ) : (
            <div className="card-field-display">
              <span className={`card-value ${!user.costoOrario || user.costoOrario <= 0 ? 'empty' : ''}`}>
                {user.costoOrario && user.costoOrario > 0 
                  ? `€${parseFloat(user.costoOrario).toFixed(2)}/ora` 
                  : '❌ Non impostato'}
              </span>
              <button
                className="btn-edit-inline"
                onClick={() => {
                  setEditingField({ userId: user.id, field: 'costoOrario' });
                  setEditingCostoOrario(prev => ({ 
                    ...prev, 
                    [user.id]: user.costoOrario ? user.costoOrario.toString() : '' 
                  }));
                }}
              >
                ✏️
              </button>
            </div>
          )}
        </div>

        {/* Sezione Scadenze */}
<div className="user-section scadenze-section">
  <h4 className="section-title">
    <span className="section-icon">📅</span>
    Scadenze e Documenti
  </h4>
  
  <div className="scadenze-grid">
    {/* Tipo Contratto */}
    <div className="form-group">
      <label className="form-label">
        <span className="label-icon">📋</span>
        Tipo Contratto
      </label>
      <select 
        className="form-select"
        value={user.tipoContratto || 'indeterminato'}
        onChange={(e) => handleTipoContrattoChange(user.id, e.target.value)}
      >
        <option value="indeterminato">Indeterminato</option>
        <option value="determinato">Determinato</option>
      </select>
    </div>
    
    {/* Data Scadenza Contratto (solo se determinato) */}
    {user.tipoContratto === 'determinato' && (
      <div className="form-group">
        <label className="form-label">
          <span className="label-icon">⚠️</span>
          Scadenza Contratto
        </label>
        <input 
          type="date" 
          className="form-input"
          value={formatDateForInput(user.dataScadenzaContratto)}
          onChange={(e) => handleDataScadenzaContrattoChange(user.id, e.target.value)}
        />
        {user.dataScadenzaContratto && (
          <small className="date-info">
            {(() => {
              const today = new Date();
              const scadenza = user.dataScadenzaContratto.toDate ? 
                user.dataScadenzaContratto.toDate() : 
                new Date(user.dataScadenzaContratto);
              const diffDays = Math.ceil((scadenza - today) / (1000 * 60 * 60 * 24));
              
              if (diffDays < 0) return <span className="text-danger">⚠️ Scaduto da {Math.abs(diffDays)} giorni</span>;
              if (diffDays <= 7) return <span className="text-danger">🔴 Scade tra {diffDays} giorni</span>;
              if (diffDays <= 30) return <span className="text-warning">🟡 Scade tra {diffDays} giorni</span>;
              return <span className="text-muted">Scade tra {diffDays} giorni</span>;
            })()}
          </small>
        )}
      </div>
    )}
    
    {/* Data Visita Medica */}
    <div className="form-group">
      <label className="form-label">
        <span className="label-icon">🏥</span>
        Scadenza Visita Medica
      </label>
      <input 
        type="date" 
        className="form-input"
        value={formatDateForInput(user.dataScadenzaVisitaMedica)}
        onChange={(e) => handleDataVisitaMedicaChange(user.id, e.target.value)}
      />
      {user.dataScadenzaVisitaMedica && (
        <small className="date-info">
          {(() => {
            const today = new Date();
            const scadenza = user.dataScadenzaVisitaMedica.toDate ? 
              user.dataScadenzaVisitaMedica.toDate() : 
              new Date(user.dataScadenzaVisitaMedica);
            const diffDays = Math.ceil((scadenza - today) / (1000 * 60 * 60 * 24));
            
            if (diffDays < 0) return <span className="text-danger">⚠️ Scaduta da {Math.abs(diffDays)} giorni</span>;
            if (diffDays <= 7) return <span className="text-danger">🔴 Scade tra {diffDays} giorni</span>;
            if (diffDays <= 30) return <span className="text-warning">🟡 Scade tra {diffDays} giorni</span>;
            return <span className="text-muted">Scade tra {diffDays} giorni</span>;
          })()}
        </small>
      )}
    </div>
  </div>
</div>

        {/* Azioni Card */}
        {!isEditingIncarico && !isEditingCosto && !isEditingRoleField && !isEditingAziendaField && 
         (user.incarico || (user.costoOrario && user.costoOrario > 0) || user.azienda) && (
          <div className="card-actions-footer">
            <button
              className="btn-danger btn-full"
              onClick={() => {
                setConfirmModal({
                  isOpen: true,
                  title: 'Reset Dati Dipendente',
                  message: `Vuoi rimuovere incarico, costo orario e azienda di ${fullName}?`,
                  onConfirm: async () => {
                    try {
                      const userRef = doc(db, "users", user.id);
                      await updateDoc(userRef, { 
                        incarico: '',
                        costoOrario: 0,
                        azienda: '' // ← NUOVO
                      });
                      setUsers(prevUsers => 
                        prevUsers.map(u => 
                          u.id === user.id 
                            ? { ...u, incarico: '', costoOrario: 0, azienda: '' } 
                            : u
                        )
                      );
                      showNotification(`Dati rimossi per ${fullName}`, 'success');
                    } catch (err) {
                      showNotification('Errore nella rimozione', 'error');
                    }
                  }
                });
              }}
            >
              🗑️ Reset Tutto
            </button>
          </div>
        )}
      </div>
    );
  })}
</div>
        </>
      )}

      {/* MODAL DI CONFERMA */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type="danger"
      />
    </div>
  );
};

export default IncarichiManagement;